import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import Cookie from 'js-cookie';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(() => {
    try {
      const token = Cookie.get('token');
      
      if (!token) {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return false;
      }

      const decodedToken = jwtDecode(token);
      const isTokenExpired = decodedToken.exp < Date.now() / 1000;

      if (isTokenExpired) {
        Cookie.remove('token');
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return false;
      }

      setUser(decodedToken);
      setIsAuthenticated(true);
      setIsLoading(false);
      return true;
    } catch (error) {
      Cookie.remove('token');
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      return false;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((token) => {
    Cookie.set('token', token, { expires: 1 });
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(() => {
    Cookie.remove('token');
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
  }, []);

  const getToken = useCallback(() => {
    return Cookie.get('token');
  }, []);

  const getUserId = useCallback(() => {
    return user?.userId || null;
  }, [user]);

  const getUserName = useCallback(() => {
    return user?.name || '';
  }, [user]);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
    getToken,
    getUserId,
    getUserName
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default useAuth;
